// Broadleaf rainforest tree geometry, generated once per species and then
// thin-instanced around the circuit. Pure functions (no Babylon objects), so
// the triangle budget can be tested without a GPU.
//
// A tree is a tapered, slightly bent trunk with a root flare, a branch out to
// each leaf clump, and each clump is four alpha-cut cards (three crossed,
// one roughly horizontal so the canopy has an underside when seen from the
// car). Card normals point away from the clump and crown centres ("sphere
// normals"), so the canopy lights as a volume instead of as flat cards, and
// vertex colours darken the inside and underside of the crown.
//
// Wood and leaves share one texture atlas (see vegetationTextures.ts), so a
// whole species is a single draw call.

export function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A rectangle of the atlas canvas, in pixels (y down). */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const ATLAS_SIZE = 512;
/** Three leaf-cluster sprites (transparent around the cluster). */
export const LEAF_SPRITES: readonly Rect[] = [
  { x: 0, y: 0, w: 256, h: 256 },
  { x: 256, y: 0, w: 256, h: 256 },
  { x: 0, y: 256, w: 256, h: 256 },
];
/** Opaque bark, inset from its quadrant so mipmaps don't bleed leaf alpha into it. */
export const BARK_SPRITE: Rect = { x: 276, y: 276, w: 216, h: 216 };
/** Leaf sprites are sampled this many pixels inside their cell, away from the neighbours. */
const LEAF_INSET = 6;

export interface TreeSpecies {
  /** Overall height, metres. */
  height: number;
  /** Height of the crown's lowest clumps as a fraction of the height. */
  crownBase: number;
  /** Horizontal crown radius, metres. */
  crownRadius: number;
  /** Number of leaf clumps (one sits on top of the leader). */
  clumps: number;
  /** Clump radius range, metres. */
  clumpRadius: [number, number];
  /** Vertical squash of the clumps (1 = round). */
  flatten: number;
  /** Trunk radius at breast height, metres. */
  trunkRadius: number;
  seed: number;
}

/** Tall emergent with an umbrella crown, a rounder mid-storey tree, and a small bushy one. */
export const BROADLEAF_SPECIES: readonly TreeSpecies[] = [
  { height: 22, crownBase: 0.56, crownRadius: 9, clumps: 8, clumpRadius: [3.3, 4.4], flatten: 0.62, trunkRadius: 0.42, seed: 101 },
  { height: 15, crownBase: 0.32, crownRadius: 6.4, clumps: 7, clumpRadius: [2.8, 3.6], flatten: 0.8, trunkRadius: 0.32, seed: 202 },
  { height: 10, crownBase: 0.22, crownRadius: 4.5, clumps: 5, clumpRadius: [2.2, 2.8], flatten: 0.86, trunkRadius: 0.22, seed: 303 },
];

export interface MeshArrays {
  positions: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
  indices: number[];
}

type V3 = [number, number, number];

const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const scale = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k];
const lerp = (a: V3, b: V3, t: number): V3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const len = (a: V3): number => Math.hypot(a[0], a[1], a[2]);
const norm = (a: V3): V3 => {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

/** Atlas UV for a point (s, t) of a sprite, t = 0 at the sprite's top. The canvas top is v = 1. */
function atlasUV(r: Rect, s: number, t: number): [number, number] {
  return [(r.x + s * r.w) / ATLAS_SIZE, 1 - (r.y + t * r.h) / ATLAS_SIZE];
}

class Builder {
  readonly a: MeshArrays = { positions: [], normals: [], uvs: [], colors: [], indices: [] };

  vertex(p: V3, n: V3, uv: [number, number], shade: number): number {
    const a = this.a;
    a.positions.push(p[0], p[1], p[2]);
    a.normals.push(n[0], n[1], n[2]);
    a.uvs.push(uv[0], uv[1]);
    a.colors.push(shade, shade, shade, 1);
    return a.positions.length / 3 - 1;
  }

  tri(i: number, j: number, k: number): void {
    this.a.indices.push(i, j, k);
  }

  /**
   * A tube along `spine` with `sides` faces, radius per spine point, bark UVs
   * (v runs up the tube) and shade per spine point.
   */
  tube(spine: V3[], radii: number[], shades: number[], sides: number, vSpan: number): void {
    const first = this.a.positions.length / 3;
    let along = 0;
    for (let k = 0; k < spine.length; k++) {
      if (k > 0) along += len(sub(spine[k]!, spine[k - 1]!));
      // Frame perpendicular to the local direction.
      const dir = norm(sub(spine[Math.min(k + 1, spine.length - 1)]!, spine[Math.max(k - 1, 0)]!));
      const ref: V3 = Math.abs(dir[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0];
      const u = norm(cross(dir, ref));
      const w = cross(dir, u);
      for (let j = 0; j <= sides; j++) {
        const a = (j / sides) * Math.PI * 2;
        const n: V3 = add(scale(u, Math.cos(a)), scale(w, Math.sin(a)));
        this.vertex(add(spine[k]!, scale(n, radii[k]!)), n, atlasUV(BARK_SPRITE, j / sides, 1 - clamp01(along / vSpan)), shades[k]!);
      }
    }
    const row = sides + 1;
    for (let k = 0; k < spine.length - 1; k++) {
      for (let j = 0; j < sides; j++) {
        const a = first + k * row + j;
        const b = a + row;
        this.tri(a, b, a + 1);
        this.tri(a + 1, b, b + 1);
      }
    }
  }
}

interface Clump {
  c: V3;
  r: number;
}

/** Geometry for one broadleaf species, base at the origin, y up. */
export function broadleafGeometry(sp: TreeSpecies): MeshArrays {
  const rand = rng(sp.seed);
  const b = new Builder();
  const H = sp.height;
  const crownBottom = H * sp.crownBase;
  const crownMid = (crownBottom + H) / 2;
  const crownCentre: V3 = [0, crownMid, 0];

  // Clumps: one on top of the leader, the rest spread over a squashed shell,
  // the lower ones further out (a spreading, layered crown).
  const clumps: Clump[] = [];
  const [rMin, rMax] = sp.clumpRadius;
  const lean: V3 = [(rand() - 0.5) * 0.8, 0, (rand() - 0.5) * 0.8];
  clumps.push({ c: add([0, H - rMax * sp.flatten, 0], lean), r: rMax });
  const ring = sp.clumps - 1;
  for (let i = 0; i < ring; i++) {
    const t = ring > 1 ? i / (ring - 1) : 0.5;
    const a = i * 2.39996 + rand() * 0.6;
    const r = rMin + rand() * (rMax - rMin);
    // Low tier: wide and out; high tier: closer to the leader.
    const out = sp.crownRadius * (0.92 - 0.42 * t) - r * 0.55;
    const y = crownBottom + r * sp.flatten * 0.8 + (H - crownBottom - r * sp.flatten * 2.2) * t + (rand() - 0.5) * 0.8;
    clumps.push({ c: [Math.cos(a) * out, y, Math.sin(a) * out], r });
  }
  const lowest = Math.min(...clumps.map((k) => k.c[1] - k.r * sp.flatten));

  /** Ambient occlusion: dark low in the crown and near the trunk, bright on top and outside. */
  const shadeAt = (p: V3): number => {
    const h = clamp01((p[1] - lowest) / (H - lowest));
    const outer = clamp01(Math.hypot(p[0], p[2]) / sp.crownRadius);
    return Math.min(1, 0.42 + 0.42 * h + 0.22 * outer);
  };

  // Trunk: root flare, slight bend, leader up into the top clump.
  const top = clumps[0]!.c;
  const trunkTop = top[1] - clumps[0]!.r * 0.3;
  const bend: V3 = [(rand() - 0.5) * 0.6, 0, (rand() - 0.5) * 0.6];
  const spine: V3[] = [
    [0, 0, 0],
    [0, 0.8, 0],
    add([0, trunkTop * 0.55, 0], bend),
    [top[0], trunkTop, top[2]],
  ];
  const R = sp.trunkRadius;
  b.tube(spine, [R * 1.9, R * 1.12, R * 0.85, R * 0.4], [0.82, 0.8, 0.66, 0.5], 6, H * 0.6);

  /** Point on the trunk spine at height y. */
  const trunkAt = (y: number): V3 => {
    for (let k = 1; k < spine.length; k++) {
      const p0 = spine[k - 1]!;
      const p1 = spine[k]!;
      if (y <= p1[1]) return lerp(p0, p1, clamp01((y - p0[1]) / (p1[1] - p0[1])));
    }
    return spine[spine.length - 1]!;
  };

  // A branch from the trunk to every outer clump, rising then levelling out.
  for (let i = 1; i < clumps.length; i++) {
    const { c, r } = clumps[i]!;
    const horiz = Math.hypot(c[0], c[2]);
    const y0 = Math.max(crownBottom - 1.5, c[1] - horiz * (0.55 + rand() * 0.3));
    const base = trunkAt(Math.min(y0, trunkTop - 0.5));
    const tip = lerp(c, crownCentre, 0.12);
    const mid = add(lerp(base, tip, 0.5), [0, (tip[1] - base[1]) * 0.25 + 0.2 * horiz * 0.15, 0]);
    const br = R * (0.32 + 0.1 * (r / rMax));
    b.tube([base, mid, tip], [br, br * 0.65, br * 0.25], [0.62, 0.55, 0.5], 3, H * 0.6);
  }

  // Leaf cards.
  for (const { c, r } of clumps) {
    const out = norm(sub(c, crownCentre));
    const yaw0 = rand() * Math.PI;
    const card = (centre: V3, right: V3, up: V3, w: number, h: number, under: boolean): void => {
      const sprite = LEAF_SPRITES[Math.floor(rand() * LEAF_SPRITES.length)]!;
      const flip = rand() < 0.5;
      const ids: number[] = [];
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const) {
        const p = add(centre, add(scale(right, (sx * w) / 2), scale(up, (sy * h) / 2)));
        const n = norm(add(add(scale(norm(sub(p, c)), 0.65), scale(out, 0.55)), [0, under ? -0.15 : 0.2, 0]));
        const e = LEAF_INSET / sprite.w;
        const s = e + (1 - 2 * e) * (flip ? (1 - sx) / 2 : (sx + 1) / 2);
        const uv = atlasUV(sprite, s, e + (1 - 2 * e) * ((1 - sy) / 2));
        ids.push(b.vertex(p, n, uv, shadeAt(p) * (under ? 0.78 : 1)));
      }
      b.tri(ids[0]!, ids[1]!, ids[2]!);
      b.tri(ids[0]!, ids[2]!, ids[3]!);
    };
    const size = r * 2.4;
    // Three crossed, tilted cards...
    for (let k = 0; k < 3; k++) {
      const yaw = yaw0 + (k * Math.PI) / 3;
      const tilt = (rand() - 0.5) * 0.7;
      const right: V3 = [Math.cos(yaw), 0, Math.sin(yaw)];
      const flat: V3 = [-Math.sin(yaw), 0, Math.cos(yaw)];
      const up = norm(add([0, Math.cos(tilt), 0], scale(flat, Math.sin(tilt))));
      card(c, right, up, size, size * sp.flatten, false);
    }
    // ...and one lying nearly flat, slightly low: the clump's underside.
    const yaw = rand() * Math.PI;
    const tilt = (rand() - 0.5) * 0.5;
    const right = norm([Math.cos(yaw), Math.sin(tilt), Math.sin(yaw)]);
    const fwd = norm(add(cross(right, [0, 1, 0]), [0, Math.sin((rand() - 0.5) * 0.5), 0]));
    card(add(c, [0, -r * sp.flatten * 0.25, 0]), right, fwd, size * 0.95, size * 0.95, true);
  }
  return b.a;
}

/** Triangle count of a mesh. */
export function triangles(a: MeshArrays): number {
  return a.indices.length / 3;
}
